/**
 * Phase 5 PR 5.2 — `useGenerateMacroPlan` hook.
 *
 * Triggers the AI-driven macro-plan generation via
 * `POST /api/macro-plan/generate` (handler at `api/index.js:4641
 * handleGenerateMacroPlan`). The endpoint:
 *
 *   1. Loads the season + meets + athlete context
 *   2. Computes plan range (TODAY → last championship + 2 recovery weeks)
 *   3. Calls the LLM to produce a phase plan (5–15s typical)
 *   4. Inserts `season_macro_plan` rows
 *   5. Sets `seasons.macro_plan_status = 'proposed'`
 *
 * Response: `{ success: true, weekCount: N, notes: string }` on success.
 *
 * Generate UX rules (Phase 5 plan §"Generate UX rules"):
 *
 *   1. **Disabled while generating.** `generating === true` is the
 *      load-bearing flag the UI gates on. Prevents double-clicks
 *      during the 5–15s LLM call.
 *
 *   2. **Idempotent on duplicate.** This hook doesn't enforce —
 *      `useMacroPlan` is the source of truth. Caller hides the CTA
 *      when status !== 'none'.
 *
 *   3. **Retryable error state.** `error` and `lastError` populate
 *      on failure. Caller shows Retry. No automatic retry — the
 *      endpoint is expensive.
 *
 *   4. **Timeout-safe retry.** A 30s client-side abort means we
 *      can't tell if the backend completed. Caller's onRetry MUST
 *      first refetch `useMacroPlan` (cheap) and only call generate
 *      again if status === 'none'. We don't enforce that here —
 *      the route does (see `routes/plan.tsx`).
 *
 *   5. **No client-side rate limit needed.** Backend handles. Hook
 *      stays simple.
 *
 * Usage:
 *
 *     const { generate, generating, error, lastError } =
 *       useGenerateMacroPlan({ seasonId });
 *
 *     <button disabled={generating} onClick={generate}>...</button>
 */

import { useCallback, useRef, useState } from 'react';
import { useAuthFetch } from './useAuthFetch';
import type { CodedError } from '../lib/roleRefusal';
import { NO_ACTIVE_SEASON, NO_ACTIVE_SEASON_MESSAGE } from '../lib/planEdit';

export interface UseGenerateMacroPlanArgs {
  /** Active season id. Generation is disabled when null. */
  seasonId: string | null;
}

export interface UseGenerateMacroPlanResult {
  /**
   * Trigger a generate call. Resolves to:
   *   - `{ ok: true,  weekCount }` on success
   *   - `{ ok: false, error }`     on failure / abort / timeout
   * Never throws — the route renders failures via `lastError`.
   *
   * Season 2c: pass `{ seasonId }` to target a freshly-created season in the
   * create→generate chain (the EXPLICIT id), bypassing the hook's bound
   * seasonId so we never depend on a not-yet-refetched active-season cache.
   *
   * B13 (2026-08-21): pass `{ confirmReplan: true }` ONLY from an explicit
   * re-anchor surface (stale "Update plan" / ReanchorPrompt) whose copy says a
   * re-plan will happen. The server refuses (409 `replan_blocked_in_flight`)
   * any generate against a season with weekly plans in flight that doesn't
   * carry the flag — a season mid-flight must never be silently re-planned.
   */
  generate: (override?: { seasonId?: string; confirmReplan?: boolean }) => Promise<{
    ok: boolean;
    weekCount?: number;
    error?: Error;
  }>;
  /** True while a request is in flight. Use to disable the CTA. */
  generating: boolean;
  /** Latest in-flight error, cleared at the start of each generate. */
  error: Error | null;
  /** Persists across refetches so the route can render the retry copy
   *  even after `error` is cleared at the next generate(). */
  lastError: Error | null;
  /**
   * ⚖️ R71 — drop the sticky error. `lastError` is deliberately sticky so a
   * retry affordance survives a refetch, but the refusal LINE must clear on
   * the next successful generate and must never persist across sessions. The
   * route calls this the moment a generate succeeds.
   */
  clearError: () => void;
}

const DEFAULT_TIMEOUT_MS = 30_000;

export function useGenerateMacroPlan({
  seasonId,
}: UseGenerateMacroPlanArgs): UseGenerateMacroPlanResult {
  const fetchAuthed = useAuthFetch();
  const [generating, setGenerating] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);
  const inFlightRef = useRef<AbortController | null>(null);

  const generate = useCallback(async (override?: { seasonId?: string; confirmReplan?: boolean }) => {
    const effectiveSeasonId = override?.seasonId ?? seasonId;
    if (!effectiveSeasonId) {
      const err: CodedError = new Error(NO_ACTIVE_SEASON_MESSAGE);
      err.code = NO_ACTIVE_SEASON;
      setLastError(err);
      return { ok: false as const, error: err };
    }
    if (inFlightRef.current) {
      // Defensive: a second click while in-flight should be a no-op.
      // The disabled-while-generating UX rule prevents this from the
      // happy path, but guard at the hook layer too.
      const err = new Error('Generation already in progress');
      return { ok: false as const, error: err };
    }

    const controller = new AbortController();
    inFlightRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

    setGenerating(true);
    setError(null);
    try {
      const res = await fetchAuthed('/api/macro-plan/generate', {
        method: 'POST',
        body: JSON.stringify({
          seasonId: effectiveSeasonId,
          // B13 — only ever true from an explicit re-anchor surface; absent
          // otherwise so the server's in-flight guard fails closed.
          ...(override?.confirmReplan === true ? { confirmReplan: true } : {}),
        }),
        signal: controller.signal,
      });
      if (!res.ok) {
        // Try to read JSON error message; fall back to status code.
        let msg = `/api/macro-plan/generate responded ${res.status}`;
        let code: string | undefined;
        try {
          const body = await res.json();
          if (body && typeof body.error === 'string') msg = body.error;
          // UAT-B4 — surface the role-refusal code (→ refusal state 1e).
          if (body && typeof body.code === 'string') code = body.code;
        } catch {
          /* ignore body parse errors */
        }
        const err: CodedError = new Error(msg);
        if (code) err.code = code;
        throw err;
      }
      const json = (await res.json()) as { success?: boolean; weekCount?: number };
      if (!json.success) {
        throw new Error('Generate succeeded HTTP-wise but returned success=false');
      }
      return { ok: true as const, weekCount: json.weekCount ?? 0 };
    } catch (e) {
      const wrapped =
        e instanceof Error
          ? e
          : new Error(typeof e === 'string' ? e : 'Generate failed');
      // Mark abort errors with a friendlier message — the timeout case.
      const isAbort = wrapped.name === 'AbortError';
      const finalErr = isAbort
        ? new Error(
            'Plan generation timed out. The plan may still be saving on the server. Refresh in a moment to check.',
          )
        : wrapped;
      setError(finalErr);
      setLastError(finalErr);
      return { ok: false as const, error: finalErr };
    } finally {
      clearTimeout(timeoutId);
      inFlightRef.current = null;
      setGenerating(false);
    }
  }, [fetchAuthed, seasonId]);

  const clearError = useCallback(() => {
    setError(null);
    setLastError(null);
  }, []);

  return { generate, generating, error, lastError, clearError };
}
